import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import useSnackbar from 'utils/useSnackbar'
import useRouter from 'utils/useRouter';
import tree from 'utils/tree';
import Repositories from 'services/repositories';
import Classes from 'services/classes';
import { Page } from 'components';
import { Folders, RepositoryPage } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    '@media (max-width: 780px)': {
      '& $folders, & $repositoryDetails': {
        flexBasis: '100%',
        width: '100%',
        maxWidth: 'none',
        flexShrink: '0',
        transition: 'transform .5s ease',
        transform: 'translateX(0)'
      }
    }
  },
  openFolder: {
    '@media (max-width: 780px)': {
      '& $folders, & $repositoryDetails': {
        transform: 'translateX(-100%)'
      }
    }
  },
  folders: {
    flexBasis: 280,
    flexShrink: 0,
    flexGrow: 0,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  repositoryDetails: {
    flexGrow: 1
  }
}));

const Repository = () => {
  const classes = useStyles();
  const router = useRouter();
  const handleAlert = useSnackbar();
  const [folders, setFolders] = useState([]);
  const [repository, setRepository] = useState({ open: false, values: {} });
  const [sharedGroups, setSharedGroups] = useState([]);
  const [sharedRepos, setSharedRepos] = useState([]);
  const { token: cancelToken, cancel } = useMemo(() => Repositories.source(), []);

  useEffect(() => {
    getFolders();
    getSharedRepos();

    return () => {
      cancel('Abort');
    };
  }, []);

  useEffect(() => {
    if (!router.match.params.id) {
      setRepository({ open: false, values: {} })
    }
  }, [router.match.params.id]);

  const handleFolderOpen = (repository) => {
    getDatasets(repository)
    getSharedGroups(repository)
  };

  const handleFolderClose = () => {
    setRepository(state => ({ ...state, open: false }));
  };

  async function getFolders() {
    try {
      const { data } = await Repositories.getWithFolders({}, cancelToken);
      const find = data.repositories.find(repository => repository.id === parseInt(router.match.params.id));
      if (find) getDatasets(find);
      setFolders(tree(data.folders, data.repositories));
    } catch (err) {
      handleAlert(err);
    }
  }

  async function getDatasets(repository) {
    try {
      const { data } = await Repositories.getDatasets({ repoID: repository.id });
      setRepository({ values: { ...repository, datasets: data }, open: true });
    } catch (err) {
      handleAlert(err);
    }
  }

  async function getSharedGroups(repository) {
    try {
      const { data } = await Repositories.getSharedGroups({ repoID: repository.id });
      setSharedGroups(data);
    } catch (err) {
      handleAlert(err);
    }
  }

  const getSharedRepos = async () => {
    const data = await Repositories.getReposSharedWithMe();
    setSharedRepos(data);
  }

  const handleRefresh = async (repository) => {
    getFolders();
    getSharedRepos();
    getSharedGroups(repository);
  }

  const handleCreateFolder = (name, close) => {
    Repositories.createFolder({ name, parent: 0 })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDeleteFolder = (folderID, close) => {
    Repositories.deleteFolder({ folderID })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDuplicate = (id) => (name, close) => {
    Repositories.duplicateRepositories({ repoIDs: [id], name })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDelete = (id, close) => {
    Repositories.deleteRepositories({ repoIDs: [id] })
      .then(getFolders)
      .then(close)
      .then(() => {
        router.history.replace('/repositories')
        setRepository({ values: {}, open: false });
      })
      .catch(handleAlert);
  }

  const handleCreate = ({ folderID, ...rest }, close) => {
    Repositories.createRepository({ ...rest, folderID: folderID === true ? 0 : folderID })
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleEdit = (values, close) => {
    Repositories.updateRepository(values)
      .then(getFolders)
      .then(close)
      .catch(handleAlert);
  }

  const handleDrop = (folder, id) => {
    Repositories.changeFolder({ repositories: [id], folders: [], folder })
      .then(getFolders)
      .catch(handleAlert);
  }

  const handleAddDataset = (id) => async ({ dataFile, ...values }, close) => {
    try {
      const fd = new FormData();
      fd.append('dataFile', dataFile);
      fd.append('data', JSON.stringify({ repoIDs: [id], ...values }))
      await Repositories.createDataset(fd);
      await getDatasets(repository.values)
      handleAlert({ severity: 'success', message: 'Dataset uploaded to the repository successfully.' })
      close();
    } catch (err) {
      handleAlert(err);
    }
  }

  const handleAddDatasets = (repoID) => async ({ toAdd = [], toRemove = [] }, close) => {
    try {
      const list = repository.values?.datasets ?? [];

      const unique = (dataset, num = 1) => {
        const index = list.findIndex(el => el.name === dataset);
        if (index !== -1) {
          if (num === 1) {
            dataset = dataset.replace(/ \((.+?)\)$/g, '');
            dataset = dataset + ` (${num + 1})`;
          } else {
            dataset = dataset.replace(/ \((.+?)\)$/g, ` (${num + 1})`);
          }
          dataset = unique(dataset, num + 1);
        }
        return dataset;
      }

      for (const item of toAdd) {
        item.name = unique(item.name);
        list.push(item);
      }

      await Repositories.addApplicationDatasetsToRepo({ repoID, toAdd, toRemove });
      await getDatasets(repository.values)
      close();
    } catch (err) {
      handleAlert(err);
    }
  }

  const handleShareRepository = async (isShared, classroomID) => {
    try {
      if (isShared) {
        await Classes.removeRepository({ classIDs: [classroomID], repoID: [repository.values.id] })
      } else {
        await Classes.addRepository({ classIDs: [classroomID], repoID: [repository.values.id] })
      }
      setSharedGroups((state) => {
        const newState = [...state];
        const foundGroupIndex = newState.findIndex((data) => data.classroomID === classroomID)
        if(foundGroupIndex >= 0) {
          newState[foundGroupIndex].isShared = !isShared;
        }
        return newState;
      });
    } catch (err) {
      handleAlert(err)
    }
  }

  return (
    <Page
      className={clsx({
        [classes.root]: true,
        [classes.openFolder]: repository.open
      })}
      title="Repositories"
    >
      <Folders
        className={classes.folders}
        create={handleCreate}
        createFolder={handleCreateFolder}
        deleteFolder={handleDeleteFolder}
        duplicate={handleDuplicate}
        folders={folders}
        onDropFolder={handleDrop}
        onFolderOpen={handleFolderOpen}
      />
      <RepositoryPage
        className={classes.repositoryDetails}
        onAdd={handleAddDataset}
        onAddDatasets={handleAddDatasets}
        onBack={handleFolderClose}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onRefresh={handleRefresh}
        repository={repository}
        sharedRepos={sharedRepos}
        sharedGroups={sharedGroups}
        onShareRepository={handleShareRepository}
      />
    </Page>
  );
};

export default Repository;
